import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { MAPBOX_API_KEY } from '../../utils/ClientsideApiKeys';
import { debounce } from '../../utils/PerformanceUtils';

import mapboxgl from 'mapbox-gl';
import { ErrorBoundary } from '@sentry/react';
import { createRoot, Root } from 'react-dom/client';
import MapGalleryMarkerPopover, {
	MapGalleryMarkerPopupPayload,
} from '../mapboxgallery/MapGalleryMarkerPopover';
import useMountEffect from '../../hooks/useMountEffect';
import '../mapboxgallery/MapboxGallery.less';
const isEqual = require('fast-deep-equal');

const CENTER_OF_UNITED_STATES = [-98.5795, 39.8283];

const DEFAULT_POPUP_RENDER_FUNC = (gle) =>
	`<div style="width: 25px; height: 25px; border-radius: 50%;"><img width=100%" height="auto" src="${gle.logoURL}" /></div><div>${gle.displayName}</div>`;
const DEFAULT_LONGITUDE_ACCESSOR = (el) => el.longitude;
const DEFAULT_LATITUDE_ACCESSOR = (el) => el.latitude;

function hasMapCoordinate(value) {
	if (value === '' || value == null) {
		return false;
	}
	const numericValue = Number(value);
	return Number.isFinite(numericValue);
}

const LocationsMapboxGalleryComponent: React.FC<any> = (props) => {
	const mapRef = useRef<any>(null);
	const mapContainerRef = useRef<any>(null);
	const mapResizeObserverRef = useRef<ResizeObserver | null>(null);
	const resizeFrameRef = useRef<number | null>(null);
	const markerRootsRef = useRef<Root[]>([]);
	// @ts-ignore
	const markersRef = useRef<mapboxgl.Marker[]>([]);
	const activeMarkerIdRef = useRef<number | null>(null);
	const markerPopupPayloadsRef = useRef<MapGalleryMarkerPopupPayload[]>([]);
	// `bounds`/`center` back the "redo search in area" action but never drive render, so they live
	// in refs; only `showSearchWithinArea` (which toggles the button) is component state.
	const boundsRef = useRef<any>({});
	const centerRef = useRef<any>(CENTER_OF_UNITED_STATES);
	// Last geolocated entities a marker build ran for — deep-compared so we only rebuild on change
	// (the function-component equivalent of the old componentDidUpdate guard).
	const prevEntitiesRef = useRef<any>(null);

	const [showSearchWithinArea, setShowSearchWithinArea] = useState(false);

	// Keep the latest prop value available to the stable (created-once) debounced moveend callback.
	const hideSearchWithinAreaRef = useRef(props.hideSearchWithinArea);
	hideSearchWithinAreaRef.current = props.hideSearchWithinArea;

	// Imperative helpers are function declarations (hoisted, so the mutually-recursive marker
	// helpers can reference each other) and operate purely on refs + their arguments, so the
	// specific closure instance a given render produces is irrelevant.
	function updateCenter() {
		if (mapRef.current) {
			const center = mapRef.current.getCenter();
			centerRef.current = [center.lng, center.lat];
		}
	}

	function observeMapResize() {
		if (!mapContainerRef.current || typeof ResizeObserver === 'undefined') {
			return;
		}
		disconnectMapResize();
		mapResizeObserverRef.current = new ResizeObserver(() => {
			if (resizeFrameRef.current !== null || typeof window === 'undefined') {
				return;
			}
			resizeFrameRef.current = window.requestAnimationFrame(() => {
				resizeFrameRef.current = null;
				if (mapRef.current) {
					mapRef.current.resize();
				}
			});
		});
		mapResizeObserverRef.current.observe(mapContainerRef.current);
	}

	function disconnectMapResize() {
		if (resizeFrameRef.current !== null && typeof window !== 'undefined') {
			window.cancelAnimationFrame(resizeFrameRef.current);
			resizeFrameRef.current = null;
		}
		if (mapResizeObserverRef.current) {
			mapResizeObserverRef.current.disconnect();
			mapResizeObserverRef.current = null;
		}
	}

	function clearMarkersAndRoots() {
		if (markersRef.current && markersRef.current.length) {
			markersRef.current.forEach((marker) => {
				marker.remove();
			});
		}
		if (markerRootsRef.current && markerRootsRef.current.length) {
			markerRootsRef.current.forEach((root) => {
				root.unmount();
			});
		}
		markersRef.current = [];
		markerRootsRef.current = [];
	}

	function handleMarkerOpenChange(markerIndex: number, open: boolean) {
		activeMarkerIdRef.current = open ? markerIndex : null;
		rerenderMarkerPopovers();
	}

	function rerenderMarkerPopovers() {
		markerRootsRef.current.forEach((root, index) => {
			const payload = markerPopupPayloadsRef.current[index];
			if (payload === undefined) return;
			root.render(
				<MapGalleryMarkerPopover
					popup={payload}
					getPopupContainer={() => mapContainerRef.current}
					isOpen={activeMarkerIdRef.current === index}
					onOpenChange={(open) => handleMarkerOpenChange(index, open)}
				/>
			);
		});
	}

	function buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor) {
		if (!mapRef.current || !mapContainerRef.current) {
			return;
		}
		clearMarkersAndRoots();
		activeMarkerIdRef.current = null;
		markerPopupPayloadsRef.current = geolocatedEntities.map((gle) => popupRenderFunc(gle));
		markersRef.current = geolocatedEntities.map((gle, index) => {
			const mountEl = document.createElement('div');
			const root = createRoot(mountEl);
			root.render(
				<MapGalleryMarkerPopover
					popup={markerPopupPayloadsRef.current[index]}
					getPopupContainer={() => mapContainerRef.current}
					isOpen={false}
					onOpenChange={(open) => handleMarkerOpenChange(index, open)}
				/>
			);
			markerRootsRef.current.push(root);
			const marker = new mapboxgl.Marker({
				element: mountEl,
				anchor: 'bottom',
			})
				.setLngLat([longitudeAccessor(gle), latitudeAccessor(gle)])
				.addTo(mapRef.current);
			return marker;
		});
	}

	function fitMapToEntities(
		geolocatedEntities,
		longitudeAccessor,
		latitudeAccessor,
		fitBoundsPadding = 40,
		fitBoundsMaxZoom?,
		lockBoundsAfterFit = false,
		disableDragPan = false
	) {
		if (!mapRef.current) {
			return;
		}
		const filteredGeolocatedEntities = geolocatedEntities.filter((gle) => {
			const longitude = longitudeAccessor(gle);
			const latitude = latitudeAccessor(gle);
			return hasMapCoordinate(longitude) && hasMapCoordinate(latitude);
		});
		if (filteredGeolocatedEntities.length === 0) {
			return;
		}
		const minLongitude = Math.min.apply(null, filteredGeolocatedEntities.map(longitudeAccessor));
		const maxLongitude = Math.max.apply(null, filteredGeolocatedEntities.map(longitudeAccessor));
		const minLatitude = Math.min.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
		const maxLatitude = Math.max.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
		if (lockBoundsAfterFit) {
			mapRef.current.setMaxBounds(null);
		}
		const fitBoundsOptions: { padding: number; maxZoom?: number } = { padding: fitBoundsPadding };
		if (fitBoundsMaxZoom !== undefined) {
			fitBoundsOptions.maxZoom = fitBoundsMaxZoom;
		}
		mapRef.current.fitBounds(
			[
				[minLongitude, minLatitude],
				[maxLongitude, maxLatitude],
			],
			fitBoundsOptions
		);
		if (lockBoundsAfterFit) {
			mapRef.current.once('moveend', () => {
				if (mapRef.current) {
					mapRef.current.setMaxBounds(mapRef.current.getBounds());
				}
			});
		}
		if (disableDragPan) {
			mapRef.current.dragPan.disable();
		}
	}

	const handleSearchWithinArea = () => {
		props.handleSearchAgain(boundsRef.current, centerRef.current);
		setShowSearchWithinArea(false);
	};

	// Created once so the debounce timer is stable across renders; reads the latest
	// `hideSearchWithinArea` via ref to mirror the class reading `this.props` at call time.
	const debouncedMoveEndCallbackRef = useRef<any>(null);
	if (debouncedMoveEndCallbackRef.current === null) {
		debouncedMoveEndCallbackRef.current = debounce(
			(bounds) => {
				if (!hideSearchWithinAreaRef.current) {
					boundsRef.current = bounds;
					setShowSearchWithinArea(true);
				}
				updateCenter();
			},
			250,
			false
		);
	}

	useMountEffect(() => {
		const {
			startingLongitude = CENTER_OF_UNITED_STATES[0],
			startingLatitude = CENTER_OF_UNITED_STATES[1],
			startingZoomLevel = 2,
			geolocatedEntities,
			popupRenderFunc = DEFAULT_POPUP_RENDER_FUNC,
			longitudeAccessor = DEFAULT_LONGITUDE_ACCESSOR,
			latitudeAccessor = DEFAULT_LATITUDE_ACCESSOR,
			showNavControls,
			navControlPosition,
			baseLayerStyle,
			renderWorldCopies = true,
			fitBoundsPadding = 40,
			fitBoundsMaxZoom,
			lockBoundsAfterFit = false,
			disableDragPan = false,
		} = props;
		// tslint:disable-next-line
		mapboxgl.accessToken = MAPBOX_API_KEY;
		mapRef.current = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: `mapbox://styles/mapbox/${baseLayerStyle}-v9`,
			center: [startingLongitude, startingLatitude],
			zoom: startingZoomLevel,
			attributionControl: false,
			renderWorldCopies,
		});
		mapRef.current.on('moveend', () => {
			debouncedMoveEndCallbackRef.current(mapRef.current.getBounds());
		});
		if (showNavControls) {
			const navigationControl = new mapboxgl.NavigationControl({ showCompass: false });
			if (navControlPosition) {
				mapRef.current.addControl(navigationControl, navControlPosition);
			} else {
				mapRef.current.addControl(navigationControl);
			}
		}

		buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor);

		fitMapToEntities(
			geolocatedEntities,
			longitudeAccessor,
			latitudeAccessor,
			fitBoundsPadding,
			fitBoundsMaxZoom,
			lockBoundsAfterFit,
			disableDragPan
		);
		observeMapResize();
		// Seed the deep-compare baseline so the update effect below doesn't rebuild on mount.
		prevEntitiesRef.current = geolocatedEntities;

		return () => {
			disconnectMapResize();
			clearMarkersAndRoots();
			if (mapRef.current) {
				mapRef.current.remove();
			}
		};
	});

	// componentDidUpdate equivalent: rebuild + refit only when geolocatedEntities deep-changes.
	// No dependency array (runs after every commit) so the deep-compare — not React's referential
	// check — decides, exactly as the class did.
	useEffect(() => {
		if (!mapRef.current) {
			return;
		}
		const {
			geolocatedEntities,
			popupRenderFunc = DEFAULT_POPUP_RENDER_FUNC,
			longitudeAccessor = DEFAULT_LONGITUDE_ACCESSOR,
			latitudeAccessor = DEFAULT_LATITUDE_ACCESSOR,
			fitBoundsPadding = 40,
			fitBoundsMaxZoom,
			lockBoundsAfterFit = false,
			disableDragPan = false,
		} = props;
		if (!isEqual(geolocatedEntities, prevEntitiesRef.current)) {
			buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor);
			fitMapToEntities(
				geolocatedEntities,
				longitudeAccessor,
				latitudeAccessor,
				fitBoundsPadding,
				fitBoundsMaxZoom,
				lockBoundsAfterFit,
				disableDragPan
			);
		}
		prevEntitiesRef.current = geolocatedEntities;
	});

	const style = {
		position: 'absolute' as 'absolute',
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		width: '100%',
	};

	return (
		<div className="mapbox-gallery" style={style} ref={mapContainerRef}>
			{showSearchWithinArea ? (
				<Button
					style={{
						position: 'absolute',
						left: '50%',
						top: '8px',
						transform: 'translateX(-50%)',
						zIndex: 2,
					}}
					onClick={handleSearchWithinArea}
				>
					Redo search in area
				</Button>
			) : null}
		</div>
	);
};

const LocationsMapboxGallery: React.FC<any> = (props) => (
	<ErrorBoundary fallback={null}>
		<LocationsMapboxGalleryComponent {...props} />
	</ErrorBoundary>
);

export default LocationsMapboxGallery;
